import CONSTANTS from "@/config/constants";
import api from "../api";
import type { IEmail, IEmailThread, IOutboxEmail, IDeliveryStatus } from "../types";
import { logger } from "@/lib/logger";

// Common response types
interface PaginatedEmailResponse {
  emails: IEmail[];
  pagination: {
    page: number;
    pages: number;
    total: number;
  };
}

interface ScheduledEmailsResponse {
  success: boolean;
  message: string;
  emails: Array<{
    id: string;
    subject: string;
    from: { email: string; name?: string };
    to: Array<{ email: string; name?: string }>;
    scheduledSendAt: string;
    scheduleStatus: string;
    scheduleRetryCount: number;
    createdAt: string;
  }>;
  total: number;
  pagination: {
    page: number;
    limit: number;
    pages: number;
    total: number;
  };
}

const MailAPI = {
  uploadAttachment: async (
    formData: FormData,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal,
  ): Promise<{
    filename: string;
    name: string;
    contentType: string;
    size: number;
    path: string;
    url: string;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      attachment: {
        filename: string;
        name: string;
        contentType: string;
        size: number;
        path: string;
      };
      file: {
        fileUrl: string;
        path: string;
        [key: string]: unknown;
      };
    }>(`${CONSTANTS.URL.email}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const progress = Math.round((event.loaded * 100) / event.total);
        onProgress(progress);
      },
    });

    const { attachment, file } = response.data;
    logger.log("Upload response - file object:", file);
    logger.log("Upload response - attachment object:", attachment);
    const fileUrl = file?.fileUrl || file?.path || attachment.path;
    logger.log("Resolved fileUrl:", fileUrl);

    return {
      filename: attachment.filename,
      name: attachment.name,
      contentType: attachment.contentType,
      size: attachment.size,
      path: attachment.path,
      url: fileUrl,
    };
  },

  fetchEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<PaginatedEmailResponse> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<PaginatedEmailResponse>(
      `${CONSTANTS.URL.email}?${params.toString()}`,
    );
    return response.data;
  },

  // Grouped conversation list — same folder/search/sort semantics as
  // fetchEmails, but each row is a thread (`{ threadId, emails[], latestDate,
  // unreadCount }`) positioned by its most recent message, not one row per
  // message.
  fetchThreads: async (
    folder: string = "inbox",
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
    search?: string,
  ): Promise<{
    threads: IEmailThread[];
    pagination: { page: number; pages: number; total: number };
  }> => {
    const params = new URLSearchParams();
    params.append("folder", folder);
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    if (search) params.append("search", search);
    const response = await api.get<{
      threads: IEmailThread[];
      pagination: { page: number; pages: number; total: number };
    }>(`${CONSTANTS.URL.email}/threads?${params.toString()}`);
    return response.data;
  },

  fetchEmailById: async (id: string, otpToken?: string): Promise<IEmail> => {
    const url = otpToken
      ? `${CONSTANTS.URL.email}/${id}?otpToken=${otpToken}`
      : `${CONSTANTS.URL.email}/${id}`;
    const response = await api.get<IEmail>(url);
    return response.data;
  },

  fetchConfidentialEmailById: async (
    id: string,
    otpToken?: string,
  ): Promise<IEmail> => {
    const url = otpToken
      ? `${CONSTANTS.URL.email}/${id}/confidential?otpToken=${encodeURIComponent(otpToken)}`
      : `${CONSTANTS.URL.email}/${id}/confidential`;

    const response = await api.get<{
      success: boolean;
      email?: IEmail;
      data?: { email?: IEmail };
    }>(url);

    const email = response.data.email ?? response.data.data?.email;
    if (!email) {
      throw new Error("Invalid confidential email response: missing email payload");
    }

    return email;
  },

  fetchThredById: async (
    id: string,
  ): Promise<{ emails: IEmail[]; isMuted?: boolean }> => {
    const response = await api.get<{ emails: IEmail[]; isMuted?: boolean }>(
      `${CONSTANTS.URL.email}/thread/${id}`,
    );
    return response.data;
  },

  fetchSentEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<PaginatedEmailResponse> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<PaginatedEmailResponse>(
      `${CONSTANTS.URL.email}/sent?${params.toString()}`,
    );
    return response.data;
  },

  fetchStaredEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<PaginatedEmailResponse> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<PaginatedEmailResponse>(
      `${CONSTANTS.URL.email}/starred?${params.toString()}`,
    );
    return response.data;
  },

  // Defaults to last-modified order, not arrival order — a draft's
  // receivedDate is frozen at creation and never reflects later edits, so
  // sorting by it would leave a just-edited draft buried instead of at the
  // top. Matches the backend default in EmailController.getDraftEmails.
  fetchDraftEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-updatedAt",
  ): Promise<PaginatedEmailResponse> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<PaginatedEmailResponse>(
      `${CONSTANTS.URL.email}/drafts?${params.toString()}`,
    );
    return response.data;
  },

  fetchEmailByCategory: async (
    category: string,
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<PaginatedEmailResponse> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) {
      params.append("limit", limit.toString());
    }
    params.append("sort", sort);
    const response = await api.get<PaginatedEmailResponse>(
      `${CONSTANTS.URL.email}/category/${category}?${params.toString()}`,
    );
    return response.data;
  },

  fetchUnReadMailCounts: async (): Promise<any> => {
    const response = await api.get<any>(
      `${CONSTANTS.URL.email}/categories/unread-counts`,
    );
    return response.data;
  },

  fetchFolders: async (): Promise<any> => {
    const response = await api.get<any>(`${CONSTANTS.URL.email}/folders`);
    return response.data;
  },

  // Create draft email. The server always inserts a new row here — a
  // stable per-compose-session `idempotencyKey` lets a retried request
  // (lost response, double-fire) resolve to that same row instead of a
  // second one. Response envelope is { success, message, draft }, not a
  // bare IEmail, so it must be unwrapped here rather than by the caller.
  createDraft: async (
    data: Partial<IEmail> & { accountId?: string; idempotencyKey?: string },
  ): Promise<IEmail> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      draft: IEmail;
    }>(CONSTANTS.URL.email + "/draft", data);
    return response.data.draft;
  },

  // Update email
  updateEmail: async (id: string, data: Partial<IEmail>): Promise<IEmail> => {
    const response = await api.put<IEmail>(
      `${CONSTANTS.URL.email}/${id}`,
      data,
    );
    return response.data;
  },

  // Updates the draft identified by `id` in place. Never creates a new
  // draft — a 404 means the id no longer refers to one of the user's
  // drafts, and the caller must start a fresh one rather than assume a row
  // was created.
  updateDraft: async (
    id: string,
    data: Partial<IEmail> & { accountId?: string },
  ): Promise<IEmail> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      draft: IEmail;
    }>(`${CONSTANTS.URL.email}/draft/${id}`, data);
    return response.data.draft;
  },

  // Delete email. `permanent` should be set whenever this is called from the
  // Trash/Spam page, so the backend always hard-deletes regardless of the
  // email's current folder.
  deleteEmail: async (id: string, permanent = false): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(
      `${CONSTANTS.URL.email}/${id}${permanent ? "?permanent=true" : ""}`,
    );
    return response.data;
  },

  // Send email
  sendEmail: async (data: Partial<IEmail>): Promise<IEmail> => {
    const response = await api.post<IEmail>(
      `${CONSTANTS.URL.email}/send`,
      data,
    );
    return response.data;
  },

  // Mark email as read/unread
  markEmailRead: async (
    id: string | string[],
    isRead: boolean,
  ): Promise<IEmail> => {
    const response = await api.put<IEmail>(`${CONSTANTS.URL.email}/read`, {
      ids: Array.isArray(id) ? id : [id],
      isRead,
    });
    return response.data;
  },

  // Mark email as archive
  markEmailAsArchive: async (id: string | string[]): Promise<IEmail> => {
    const response = await api.put<IEmail>(`${CONSTANTS.URL.email}/archive`, {
      ids: Array.isArray(id) ? id : [id],
    });
    return response.data;
  },

  // Mark email as unarchive
  markEmailAsUnarchive: async (id: string | string[]): Promise<IEmail> => {
    const response = await api.put<IEmail>(`${CONSTANTS.URL.email}/unarchive`, {
      ids: Array.isArray(id) ? id : [id],
    });
    return response.data;
  },

  // Restore an archived or trashed email back to the Inbox
  restoreToInbox: async (
    ids: string | string[],
  ): Promise<{
    success: boolean;
    message: string;
    restoredCount: number;
    notFoundIds: string[];
  }> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      restoredCount: number;
      notFoundIds: string[];
    }>(`${CONSTANTS.URL.email}/restore`, {
      ids: Array.isArray(ids) ? ids : [ids],
    });
    return response.data;
  },

  // Star / unstar email
  starEmail: async (
    id: string,
    isStarred: boolean,
  ): Promise<{
    emails: IEmail[];
    message: string;
  }> => {
    const response = await api.put<{ emails: IEmail[]; message: string }>(
      `${CONSTANTS.URL.email}/star`,
      {
        ids: [id],
        isStarred,
      },
    );
    return response.data;
  },

  // Move email to another folder
  moveEmail: async (id: string, folder: string): Promise<IEmail> => {
    const response = await api.put<IEmail>(
      `${CONSTANTS.URL.email}/${id}/move`,
      { folder },
    );
    return response.data;
  },

  // Bulk actions (delete, mark read, move, etc.)
  bulkAction: async (
    action: string,
    emailIds: string[],
    payload?: Record<string, unknown>,
  ): Promise<{ success: boolean; updated: IEmail[] }> => {
    const response = await api.post<{
      success: boolean;
      updated: IEmail[];
    }>(`${CONSTANTS.URL.email}/bulk-action`, {
      action,
      ids: emailIds,
      ...payload,
    });
    return response.data;
  },

  getTrashEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<{
    emails: IEmail[];
    pagination: { page: number; pages: number; total: number };
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<{
      emails: IEmail[];
      pagination: { page: number; pages: number; total: number };
    }>(`${CONSTANTS.URL.email}/trash?${params.toString()}`);
    return response.data;
  },

  getArchiveEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<{
    emails: IEmail[];
    pagination: { page: number; pages: number; total: number };
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<{
      emails: IEmail[];
      pagination: { page: number; pages: number; total: number };
    }>(`${CONSTANTS.URL.email}/archive?${params.toString()}`);
    return response.data;
  },

  // Get single email details
  getEmail: async (id: string): Promise<{ email: IEmail }> => {
    const response = await api.get<{ email: IEmail }>(
      `${CONSTANTS.URL.email}/${id}`,
    );
    return response.data;
  },

  // Add one or more labels to emails in a single request
  addLabelsToEmails: async (
    emailIds: string[],
    labels: string[],
  ): Promise<{ success: boolean; updated: IEmail[] }> => {
    const response = await api.put<{
      success: boolean;
      updated: IEmail[];
    }>(`${CONSTANTS.URL.email}/labels/addLabelsToEmail`, {
      emailIds,
      labels,
    });
    return response.data;
  },

  // Remove one or more labels from emails in a single request
  removeLabelsFromEmails: async (
    emailIds: string[],
    labels: string[],
  ): Promise<{ success: boolean; updated: IEmail[] }> => {
    const response = await api.delete<{
      success: boolean;
      updated: IEmail[];
    }>(`${CONSTANTS.URL.email}/labels/removeLabelsFromEmail`, {
      data: {
        ids: emailIds,
        labels,
      },
    });
    return response.data;
  },

  // Get emails by label
  fetchEmailsByLabel: async (
    labelId: string,
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<{
    emails: IEmail[];
    pagination: { page: number; pages: number; total: number };
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<{
      emails: IEmail[];
      pagination: { page: number; pages: number; total: number };
    }>(`${CONSTANTS.URL.email}/label/${labelId}?${params.toString()}`);
    return response.data;
  },

  fetchUserCategories: async (): Promise<{
    categories: unknown[];
    pagination: {
      page: number;
      pages: number;
      total: number;
    };
  }> => {
    const response = await api.get<{
      categories: unknown[];
      pagination: {
        page: number;
        pages: number;
        total: number;
      };
    }>(`${CONSTANTS.URL.email}/categories`);
    return response.data;
  },

  // Create user category/label
  createUserCategory: async (data: {
    categoryName: string;
    color: string;
  }): Promise<{ success: boolean; category: unknown }> => {
    const response = await api.post<{ success: boolean; category: unknown }>(
      `${CONSTANTS.URL.email}/categories`,
      data,
    );
    return response.data;
  },

  // Update user category/label
  updateUserCategory: async (
    id: string,
    data: { categoryName: string; color: string },
  ): Promise<{ success: boolean; category: unknown }> => {
    const response = await api.put<{ success: boolean; category: unknown }>(
      `${CONSTANTS.URL.email}/categories/${id}`,
      data,
    );
    return response.data;
  },

  // Delete user category/label
  deleteUserCategory: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(
      `${CONSTANTS.URL.email}/categories/${id}`,
    );
    return response.data;
  },

  // Schedule email for later sending
  scheduleEmail: async (data: {
    to: Array<{ email: string; name?: string } | string>;
    cc?: Array<{ email: string; name?: string } | string>;
    bcc?: Array<{ email: string; name?: string } | string>;
    subject: string;
    body: { text?: string; html?: string };
    accountId?: string;
    scheduledSendAt: string;
    attachments?: Array<{
      filename: string;
      path: string;
      contentType: string;
      size?: number;
    }>;
  }): Promise<{
    success: boolean;
    message: string;
    emailId: string;
    scheduledSendAt: string;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      emailId: string;
      scheduledSendAt: string;
    }>(`${CONSTANTS.URL.email}/schedule`, data);
    return response.data;
  },

  // Get scheduled emails
  getScheduledEmails: async (params?: {
    page?: number;
    limit?: number;
    status?: "pending" | "sent" | "failed" | "cancelled" | "all";
    search?: string;
  }): Promise<ScheduledEmailsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);

    const url = `${CONSTANTS.URL.email}/scheduled${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await api.get<ScheduledEmailsResponse>(url);
    return response.data;
  },

  // Get schedule statistics
  getScheduleStats: async (): Promise<{
    success: boolean;
    message: string;
    stats: {
      total: number;
      pending: number;
      sent: number;
      failed: number;
      cancelled: number;
    };
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      stats: {
        total: number;
        pending: number;
        sent: number;
        failed: number;
        cancelled: number;
      };
    }>(`${CONSTANTS.URL.email}/scheduled/stats`);
    return response.data;
  },

  // Get scheduled email details
  getScheduledEmailDetails: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    email: {
      id: string;
      subject: string;
      body: { text?: string; html?: string };
      from: { email: string; name?: string };
      to: Array<{ email: string; name?: string }>;
      cc?: Array<{ email: string; name?: string }>;
      bcc?: Array<{ email: string; name?: string }>;
      attachments?: Array<{
        filename: string;
        path: string;
        contentType: string;
        size?: number;
      }>;
      scheduledSendAt: string;
      scheduleStatus: string;
      scheduleRetryCount: number;
      scheduleFailureReason?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      email: {
        id: string;
        subject: string;
        body: { text?: string; html?: string };
        from: { email: string; name?: string };
        to: Array<{ email: string; name?: string }>;
        cc?: Array<{ email: string; name?: string }>;
        bcc?: Array<{ email: string; name?: string }>;
        attachments?: Array<{
          filename: string;
          path: string;
          contentType: string;
          size?: number;
        }>;
        scheduledSendAt: string;
        scheduleStatus: string;
        scheduleRetryCount: number;
        scheduleFailureReason?: string;
        createdAt: string;
        updatedAt: string;
      };
    }>(`${CONSTANTS.URL.email}/scheduled/${emailId}`);
    return response.data;
  },

  // Reschedule email
  rescheduleEmail: async (
    emailId: string,
    scheduledSendAt: string,
  ): Promise<{
    success: boolean;
    message: string;
    emailId: string;
    newScheduledSendAt: string;
  }> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      emailId: string;
      newScheduledSendAt: string;
    }>(`${CONSTANTS.URL.email}/scheduled/${emailId}/reschedule`, {
      scheduledSendAt,
    });
    return response.data;
  },

  // Cancel scheduled email
  cancelScheduledEmail: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    emailId: string;
  }> => {
    const response = await api.delete<{
      success: boolean;
      message: string;
      emailId: string;
    }>(`${CONSTANTS.URL.email}/scheduled/${emailId}/cancel`);
    return response.data;
  },

  // ==================== CONFIDENTIAL MODE API ====================

  requestOtp: async (
    id: string,
  ): Promise<{
    success: boolean;
    message: string;
    expiresInSeconds: number;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      expiresInSeconds: number;
    }>(`${CONSTANTS.URL.email}/${id}/request-otp`);
    return response.data;
  },

  verifyOtp: async (
    id: string,
    otp: string,
  ): Promise<{
    success: boolean;
    message: string;
    otpToken: string;
    expiresInSeconds: number;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      otpToken: string;
      expiresInSeconds: number;
    }>(`${CONSTANTS.URL.email}/${id}/verify-otp`, { otp });
    return response.data;
  },

  // ==================== OUTBOX API ====================

  // Get outbox emails (pending/failed emails)
  getOutbox: async (params?: {
    page?: number;
    limit?: number;
    status?: "pending" | "failed" | "sending" | "retrying" | "bounced" | "all";
  }): Promise<{
    success: boolean;
    message: string;
    emails: IOutboxEmail[];
    total: number;
    pagination: {
      page: number;
      pages: number;
      total: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);

    const queryString = queryParams.toString();
    const url = `${CONSTANTS.URL.email}/outbox${queryString ? `?${queryString}` : ""}`;

    const response = await api.get<{
      success: boolean;
      message: string;
      emails: IOutboxEmail[];
      total: number;
      pagination: {
        page: number;
        pages: number;
        total: number;
      };
    }>(url);
    return response.data;
  },

  // Get outbox statistics
  getOutboxStats: async (): Promise<{
    success: boolean;
    message: string;
    stats: {
      total: number;
      pending: number;
      sending: number;
      failed: number;
      retrying: number;
      bounced: number;
    };
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      stats: {
        total: number;
        pending: number;
        sending: number;
        failed: number;
        retrying: number;
        bounced: number;
      };
    }>(`${CONSTANTS.URL.email}/outbox/stats`);
    return response.data;
  },

  // Retry failed outbox email
  retryOutboxEmail: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    emailId: string;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      emailId: string;
    }>(`${CONSTANTS.URL.email}/outbox/${emailId}/retry`);
    return response.data;
  },

  // Cancel outbox email
  cancelOutboxEmail: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    emailId: string;
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      emailId: string;
    }>(`${CONSTANTS.URL.email}/outbox/${emailId}/cancel`);
    return response.data;
  },

  // Get email delivery status
  getEmailDeliveryStatus: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    delivery: IDeliveryStatus;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      delivery: IDeliveryStatus;
    }>(`${CONSTANTS.URL.email}/delivery/${emailId}/status`);
    return response.data;
  },

  // Advanced search with filters
  advancedSearch: async (filters: {
    query?: string;
    from?: string;
    to?: string;
    subject?: string;
    body?: string;
    hasAttachment?: boolean;
    labels?: string;
    categories?: string;
    isRead?: boolean;
    isStarred?: boolean;
    isArchived?: boolean;
    folder?: string;
    dateFrom?: string;
    dateTo?: string;
    minSize?: number;
    maxSize?: number;
    accountId?: string;
    page?: number;
    limit?: number;
    sort?: string;
  }): Promise<{
    success: boolean;
    message: string;
    emails: IEmail[];
    pagination: {
      page: number;
      limit: number;
      pages: number;
      total: number;
    };
    filters: unknown;
  }> => {
    const params = new URLSearchParams();

    if (filters.query) params.append("query", filters.query);
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    if (filters.subject) params.append("subject", filters.subject);
    if (filters.body) params.append("body", filters.body);
    if (filters.hasAttachment !== undefined)
      params.append("hasAttachment", String(filters.hasAttachment));
    if (filters.labels) params.append("labels", filters.labels);
    if (filters.categories) params.append("categories", filters.categories);
    if (filters.isRead !== undefined)
      params.append("isRead", String(filters.isRead));
    if (filters.isStarred !== undefined)
      params.append("isStarred", String(filters.isStarred));
    if (filters.isArchived !== undefined)
      params.append("isArchived", String(filters.isArchived));
    if (filters.folder) params.append("folder", filters.folder);
    if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.append("dateTo", filters.dateTo);
    if (filters.minSize !== undefined)
      params.append("minSize", String(filters.minSize));
    if (filters.maxSize !== undefined)
      params.append("maxSize", String(filters.maxSize));
    if (filters.accountId) params.append("accountId", filters.accountId);
    if (filters.page) params.append("page", String(filters.page));
    if (filters.limit) params.append("limit", String(filters.limit));
    if (filters.sort) params.append("sort", filters.sort);

    const response = await api.get<{
      success: boolean;
      message: string;
      emails: IEmail[];
      pagination: {
        page: number;
        limit: number;
        pages: number;
        total: number;
      };
      filters: unknown;
    }>(`${CONSTANTS.URL.email}/advanced-search?${params.toString()}`);

    return response.data;
  },

  // ==================== UNDO SEND API ====================

  // Send email with undo capability
  sendEmailWithUndo: async (
    data: Partial<IEmail> & { undoWindowMs?: number; idempotencyKey?: string },
  ): Promise<{
    success: boolean;
    message: string;
    email: {
      id: string;
      subject: string;
      to: Array<{ email: string; name?: string }>;
      cc: Array<{ email: string; name?: string }>;
      bcc: Array<{ email: string; name?: string }>;
      undoWindowMs: number;
      isConfidential?: boolean;
      expiresAt?: string | null;
      requireAccessCode?: boolean;
      createdAt: string;
    };
  }> => {
    const { idempotencyKey, ...body } = data;
    const response = await api.post<{
      success: boolean;
      message: string;
      email: {
        id: string;
        subject: string;
        to: Array<{ email: string; name?: string }>;
        cc: Array<{ email: string; name?: string }>;
        bcc: Array<{ email: string; name?: string }>;
        undoWindowMs: number;
        isConfidential?: boolean;
        expiresAt?: string | null;
        requireAccessCode?: boolean;
        createdAt: string;
      };
    }>(`${CONSTANTS.URL.email}/send-with-undo`, body, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return response.data;
  },

  // Undo a pending email send
  undoSend: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    email: {
      id: string;
      status: string;
      folder: string;
    };
  }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      email: {
        id: string;
        status: string;
        folder: string;
      };
    }>(`${CONSTANTS.URL.email}/undo/${emailId}`);
    return response.data;
  },

  // Get undo status for an email
  getUndoStatus: async (
    emailId: string,
  ): Promise<{
    success: boolean;
    message: string;
    isPending: boolean;
    emailId: string;
    remainingTime?: number;
    createdAt?: string;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      isPending: boolean;
      emailId: string;
      remainingTime?: number;
      createdAt?: string;
    }>(`${CONSTANTS.URL.email}/undo/${emailId}/status`);
    return response.data;
  },

  // ==================== SPAM MANAGEMENT API ====================

  // Get spam emails
  getSpamEmails: async (
    page: number = 1,
    limit?: number,
    sort: string = "-receivedDate",
  ): Promise<{
    emails: IEmail[];
    pagination: { page: number; pages: number; total: number };
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    params.append("sort", sort);
    const response = await api.get<{
      emails: IEmail[];
      pagination: { page: number; pages: number; total: number };
    }>(`${CONSTANTS.URL.email}/spam?${params.toString()}`);
    return response.data;
  },

  // Report an email as spam
  reportSpam: async (
    id: string,
  ): Promise<{ success: boolean; message: string; email: IEmail }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      email: IEmail;
    }>(`${CONSTANTS.URL.email}/${id}/report-spam`);
    return response.data;
  },

  // Mark an email as not spam
  markNotSpam: async (
    id: string,
  ): Promise<{ success: boolean; message: string; email: IEmail }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      email: IEmail;
    }>(`${CONSTANTS.URL.email}/${id}/not-spam`);
    return response.data;
  },

  // ==================== LIST UNSUBSCRIBE API ====================

  // Trigger unsubscription via backend
  unsubscribeFromEmail: async (
    id: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      `${CONSTANTS.URL.email}/${id}/unsubscribe`,
    );
    return response.data;
  },

  // ==================== SNOOZE API ====================

  // Snooze an email until a specific time
  snoozeEmail: async (
    id: string,
    snoozedUntil: string,
  ): Promise<{ success: boolean; message: string; email: IEmail }> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      email: IEmail;
    }>(`${CONSTANTS.URL.email}/${id}/snooze`, { snoozedUntil });
    return response.data;
  },

  // Unsnooze an email
  unsnoozeEmail: async (
    id: string,
  ): Promise<{ success: boolean; message: string; email: IEmail }> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      email: IEmail;
    }>(`${CONSTANTS.URL.email}/${id}/unsnooze`);
    return response.data;
  },

  // ==================== FOLLOW-UP REMINDER API ====================

  // Set a follow-up reminder for an email
  setFollowUp: async (
    id: string,
    followUpAt: string,
  ): Promise<{ success: boolean; message: string; email: IEmail }> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      email: IEmail;
    }>(`${CONSTANTS.URL.email}/${id}/follow-up`, { followUpAt });
    return response.data;
  },

  // Cancel a follow-up reminder
  cancelFollowUp: async (
    id: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>(
      `${CONSTANTS.URL.email}/${id}/follow-up`,
    );
    return response.data;
  },

  // ==================== MUTE THREAD API ====================

  // Mute a thread
  muteThread: async (
    threadId: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put<{ success: boolean; message: string }>(
      `${CONSTANTS.URL.email}/thread/${threadId}/mute`,
    );
    return response.data;
  },

  // Unmute a thread
  unmuteThread: async (
    threadId: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put<{ success: boolean; message: string }>(
      `${CONSTANTS.URL.email}/thread/${threadId}/unmute`,
    );
    return response.data;
  },

  // Get all pending emails for the user
  getPendingEmails: async (): Promise<{
    success: boolean;
    message: string;
    pendingEmails: Array<{
      id: string;
      subject: string;
      to: Array<{ email: string; name?: string }>;
      cc: Array<{ email: string; name?: string }>;
      bcc: Array<{ email: string; name?: string }>;
      remainingTime: number;
      createdAt: string;
    }>;
    count: number;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      pendingEmails: Array<{
        id: string;
        subject: string;
        to: Array<{ email: string; name?: string }>;
        cc: Array<{ email: string; name?: string }>;
        bcc: Array<{ email: string; name?: string }>;
        remainingTime: number;
        createdAt: string;
      }>;
      count: number;
    }>(`${CONSTANTS.URL.email}/pending`);
    return response.data;
  },
};

export default MailAPI;
